// Small pieces shared by the trade tabs on the Sales page.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Pill } from '../lib/ui';
import { STAGE, INVOICE_STATE, invoiceState, followUpState, relativeDay, formatDay, todayYmd } from '../lib/trade';

// A request with no response at all (axios's bare "Network Error") is either
// a dropped connection or a server that took longer than nginx's 60s.
export const errorText = (e) => e?.response?.data?.error
  || (e?.request && !e?.response ? 'No answer from the server: the connection dropped or it took too long. Try again.' : '')
  || e?.message || 'Something went wrong';

// A short-lived message in the corner. flash(text, tone) with tone ok | bad | warn | info.
export function useFlash() {
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);
  const flash = useCallback((text, tone = 'ok') => {
    clearTimeout(timer.current);
    setMsg({ text, tone });
    timer.current = setTimeout(() => setMsg(null), tone === 'bad' ? 9000 : 4500);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  const banner = msg && (
    <div className={`ui-banner ui-banner--${msg.tone} trade-flash`} role="status" onClick={() => setMsg(null)}>
      {msg.text}
    </div>
  );
  return { flash, banner };
}

export const StagePill = ({ stage }) => (
  <Pill tone={STAGE[stage]?.tone}>{STAGE[stage]?.label || stage}</Pill>
);

export function InvoicePill({ invoice, today = todayYmd() }) {
  const state = invoiceState(invoice, today);
  return <Pill tone={INVOICE_STATE[state].tone}>{INVOICE_STATE[state].label}</Pill>;
}

// "Overdue 3 days ago" / "Today" / "Thu 23 Oct" tag for a business's next step
export function FollowUpTag({ account, today = todayYmd() }) {
  const state = followUpState(account, today);
  if (!state || state === 'undated') return null;
  const text = state === 'upcoming'
    ? formatDay(account.nextStepDate, { weekday: true }).replace(/ \d{4}$/, '')
    : state === 'today' ? 'Today' : `Overdue, ${relativeDay(account.nextStepDate, today)}`;
  return <span className={`trade-when is-${state}`}>{text}</span>;
}

export const Label = ({ children, hint }) => (
  <span className="trade-label">{children}{hint && <small> ({hint})</small>}</span>
);
